import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Header from "../Home/Header";
import Footers from "../Home/Footers";
import "./Company.css";
import "../Home/publicPages.css";
import "../Home/catalogHero.css";
import AxiosInstance from "../Home/AxiosInstance";
import "bootstrap/dist/css/bootstrap.min.css";
import { FiSearch, FiX } from "react-icons/fi";
import {
  CatalogFilters,
  FilterGroup,
  FilterCheckbox,
  toggleFilterValue,
  buildCompanyFilterOptions,
  matchesExactValues,
} from "../Home/CatalogFilters";

const INITIAL_COLORS = [
  "#5b5fff",
  "#0ea5e9",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#8b5cf6",
  "#14b8a6",
  "#f97316",
];

const getLogoUrl = (logo) => {
  if (!logo) return null;
  const path = String(logo).replace(/\\/g, "/");
  if (path.startsWith("http")) return path;
  return `${AxiosInstance.defaults.baseURL}/media/${path}`;
};

const getInitialColor = (name) => {
  const code = (name || "A").charCodeAt(0) || 65;
  return INITIAL_COLORS[code % INITIAL_COLORS.length];
};

export default function Company() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [filterBusinessTypes, setFilterBusinessTypes] = useState([]);
  const [filterCities, setFilterCities] = useState([]);
  const companiesPerPage = 9;

  const availableFilters = useMemo(
    () => buildCompanyFilterOptions(companies),
    [companies]
  );

  const filteredCompanies = companies.filter((inst) => {
    const searchMatch =
      inst.company_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (inst.email &&
        inst.email.toLowerCase().includes(searchTerm.toLowerCase()));
    return (
      searchMatch &&
      matchesExactValues(
        inst.business_type?.business_type,
        filterBusinessTypes
      ) &&
      matchesExactValues(inst.city, filterCities)
    );
  });

  const clearSideFilters = () => {
    setFilterBusinessTypes([]);
    setFilterCities([]);
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  const indexOfLastCompany = currentPage * companiesPerPage;
  const indexOfFirstCompany = indexOfLastCompany - companiesPerPage;
  const currentCompanies = filteredCompanies.slice(
    indexOfFirstCompany,
    indexOfLastCompany
  );
  const totalPages = Math.ceil(filteredCompanies.length / companiesPerPage);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        const response = await AxiosInstance.get("companies/companies");
        const data = response.data;

        const filtered = data.filter(
          (company) =>
            company.business_type &&
            company.business_type.business_type.toLowerCase() !== "institutions"
        );

        const enriched = await Promise.all(
          filtered.map(async (company) => {
            let city = "";
            try {
              const addrRes = await AxiosInstance.get(
                `companies/company/addresses/${company.company_id}/`
              );
              const addresses = Array.isArray(addrRes.data) ? addrRes.data : [];
              city = addresses[0]?.city || "";
            } catch {
              /* no address */
            }
            return { ...company, city };
          })
        );

        setCompanies(enriched);
      } catch (error) {
        console.error("Error fetching Companies:", error);
      }
    };

    fetchCompanies();
  }, []);

  const handleCompanySearch = () => {
    setSearchTerm(searchInput);
    setCurrentPage(1);
  };

  const clearCompanySearch = () => {
    setSearchInput("");
    setSearchTerm("");
    setCurrentPage(1);
  };

  return (
    <div className="EmployerPage min-vh-100 d-flex flex-column">
      <Header />
      <div className="als-page-hero als-hero-split">
        <div className="als-page-hero-inner">
          <div className="als-hero-copy">
            <h1>Hire talent. Build the future.</h1>
            <p>
              Partner with Open Learnings to reach job-ready students and grow your
              next generation team.
            </p>
            <div className="als-hero-stats">
              <span>
                <strong>{Math.max(companies.length, 50)}+</strong>
                Companies
              </span>
              <span>
                <strong>3200+</strong>
                Internships Posted
              </span>
            </div>
          </div>
          <div className="als-hero-media">
            <img
              src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=800&h=520&fit=crop"
              alt="Modern workplace"
            />
          </div>
        </div>
      </div>

      <div className="mp-catalog flex-grow-1">
        <div className="mp-catalog-inner">
          <form
            className="mp-search-bar"
            onSubmit={(e) => {
              e.preventDefault();
              handleCompanySearch();
            }}
          >
            <div className="mp-search-field">
              <FiSearch className="mp-search-icon" />
              <input
                type="text"
                placeholder="Search by company name or email"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              {searchInput && (
                <button
                  type="button"
                  className="mp-clear-input"
                  onClick={clearCompanySearch}
                  aria-label="Clear company search"
                >
                  <FiX />
                </button>
              )}
            </div>
            <button type="submit" className="mp-search-btn">
              Search
            </button>
          </form>

          <div className="mp-layout mp-layout-filters">
            <CatalogFilters onClear={clearSideFilters}>
              {availableFilters.businessTypes.length > 0 && (
                <FilterGroup title="Business type">
                  {availableFilters.businessTypes.map((type) => (
                    <FilterCheckbox
                      key={type}
                      label={type}
                      checked={filterBusinessTypes.includes(type)}
                      onChange={() =>
                        setFilterBusinessTypes((prev) =>
                          toggleFilterValue(prev, type)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
              {availableFilters.cities.length > 0 && (
                <FilterGroup title="Location">
                  {availableFilters.cities.map((city) => (
                    <FilterCheckbox
                      key={city}
                      label={city}
                      checked={filterCities.includes(city)}
                      onChange={() =>
                        setFilterCities((prev) =>
                          toggleFilterValue(prev, city)
                        )
                      }
                    />
                  ))}
                </FilterGroup>
              )}
            </CatalogFilters>

            <div className="mp-main">
              <div className="mp-results-bar">
                <h2>
                  {filteredCompanies.length} compan
                  {filteredCompanies.length === 1 ? "y" : "ies"} found
                </h2>
              </div>

              {currentCompanies.length > 0 ? (
                <div className="mp-list">
                  {currentCompanies.map((comp) => {
                    const logoUrl = getLogoUrl(comp.company_logo);
                    const initial = (comp.company_name || "?").charAt(0).toUpperCase();
                    const accent = getInitialColor(comp.company_name);

                    return (
                      <article key={comp.company_id} className="mp-list-card">
                        <div className="mp-list-top">
                          <div
                            className="mp-list-logo"
                            style={
                              !logoUrl
                                ? { background: `${accent}1a`, color: accent }
                                : undefined
                            }
                            aria-hidden="true"
                          >
                            {logoUrl ? (
                              <img
                                src={logoUrl}
                                alt=""
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              initial
                            )}
                          </div>
                          <div className="mp-list-heading">
                            <h3>{comp.company_name}</h3>
                            {comp.email && (
                              <p className="mp-list-sub">{comp.email}</p>
                            )}
                            <div className="mp-list-footer">
                              {comp.business_type?.business_type ||
                              comp.website ? (
                                <p className="mp-list-about">
                                  {comp.business_type?.business_type ||
                                    comp.website}
                                </p>
                              ) : (
                                <span />
                              )}
                              <div className="mp-list-action">
                                <button
                                  type="button"
                                  className="mp-view-btn"
                                  onClick={() =>
                                    navigate(`/employer/${comp.company_id}`)
                                  }
                                >
                                  View Profile
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </article>
                    );
                  })}
                </div>
              ) : (
                <div className="mp-empty">
                  <h3>No companies found</h3>
                  <p>Try a different name or email search.</p>
                </div>
              )}

              {totalPages > 1 && (
                <div className="mp-pagination">
                  {[...Array(totalPages)].map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      className={`mp-page-btn${
                        currentPage === idx + 1 ? " active" : ""
                      }`}
                      onClick={() => setCurrentPage(idx + 1)}
                    >
                      {idx + 1}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footers />
    </div>
  );
}
